// Offline-first layer for notes, trash, reminders, wallpaper & notification settings.
// GETs are cached; mutations made offline are applied to the cache, queued, and
// replayed in order when back online (server resolves last-write-wins via X-Client-Ts).
import axios from "axios";
import { idb } from "@/lib/idb";
import { reduce } from "@/lib/offlineReducers";

const CACHEABLE = [/^\/auth\/me$/, /^\/titles$/, /^\/titles\/[^/]+\/items$/, /^\/trash$/, /^\/reminders$/, /^\/push\/prefs$/, /^\/user\/settings$/, /^\/reminder-sounds$/];
const QUEUEABLE = [
  ["POST", /^\/titles$/], ["PATCH", /^\/titles\/[^/]+$/], ["DELETE", /^\/titles\/[^/]+$/], ["POST", /^\/titles\/reorder$/],
  ["POST", /^\/titles\/[^/]+\/items$/], ["POST", /^\/titles\/[^/]+\/items\/reorder$/], ["POST", /^\/titles\/[^/]+\/uploads$/],
  ["PATCH", /^\/items\/[^/]+$/], ["DELETE", /^\/items\/[^/]+$/],
  ["POST", /^\/trash\/[^/]+\/restore$/], ["DELETE", /^\/trash\/[^/]+$/], ["POST", /^\/trash\/empty$/],
  ["POST", /^\/reminders$/], ["PUT", /^\/reminders\/[^/]+$/], ["DELETE", /^\/reminders\/[^/]+$/],
  ["POST", /^\/push\/prefs$/], ["PUT", /^\/user\/settings$/], ["POST", /^\/auth\/logout$/],
];
const CREATE_IDS = [[/^\/titles$/, "title_id", "title_"], [/^\/titles\/[^/]+\/items$/, "item_id", "item_"], [/^\/titles\/[^/]+\/uploads$/, "item_id", "item_"], [/^\/reminders$/, "reminder_id", "rem_"]];

let apiBase = "";
let tokenFn = () => null;
let flushing = false;
const listeners = new Set();
const state = { online: typeof navigator === "undefined" ? true : navigator.onLine, pending: 0, syncing: false };
const localUrls = new Map();

const emit = () => listeners.forEach((fn) => fn({ ...state }));
export const subscribeOffline = (fn) => { listeners.add(fn); fn({ ...state }); return () => listeners.delete(fn); };
export const getLocalFileUrl = (itemId) => localUrls.get(itemId) || null;

const pathOf = (config) => {
  let u = config.url || "";
  if (u.startsWith(apiBase)) u = u.slice(apiBase.length);
  return u.split("?")[0];
};
const isNetworkError = (e) => !e?.response && e?.code !== "ERR_CANCELED";
const genId = (prefix) => prefix + Array.from(crypto.getRandomValues(new Uint8Array(6))).map((b) => b.toString(16).padStart(2, "0")).join("");

async function refreshPending() {
  state.pending = (await idb.keys("outbox")).length;
  emit();
}

async function applyToCache(op, resp) {
  const keys = await idb.keys("cache");
  const store = new Map();
  for (const k of keys) store.set(k, parseJson(await idb.get("cache", k)));
  const changed = new Set();
  const view = { get: (k) => store.get(k), set: (k, v) => { store.set(k, v); changed.add(k); }, keys: () => [...store.keys()] };
  const result = reduce(op, view, resp);
  for (const k of changed) await idb.set("cache", k, store.get(k));
  return result;
}

// Re-apply still-queued edits on top of fresh server data so nothing "jumps back" before sync.
async function overlayPending(path, data) {
  const ops = await idb.all("outbox");
  if (!ops.length) return data;
  const store = new Map([[path, data]]);
  const view = { get: (k) => store.get(k), set: (k, v) => store.set(k, v), keys: () => [...store.keys()] };
  ops.forEach((op) => reduce(op, view));
  return store.get(path);
}

async function buildOp(config, method, path) {
  const op = { method, path, ts: Date.now(), token: tokenFn() };
  if (config.data instanceof FormData) {
    const fields = {};
    let file = null;
    for (const [k, v] of config.data.entries()) {
      if (v instanceof Blob) file = v; else fields[k] = v;
    }
    fields.item_id = fields.item_id || genId("item_");
    if (file) {
      await idb.set("blobs", fields.item_id, file);
      localUrls.set(fields.item_id, URL.createObjectURL(file));
      fields.file_name = file.name || "file";
      fields.mime_type = file.type || "application/octet-stream";
      fields.size = file.size;
    }
    op.form = fields;
    return op;
  }
  let body = config.data;
  if (typeof body === "string") { try { body = JSON.parse(body); } catch { /* keep string */ } }
  body = body && typeof body === "object" ? { ...body } : body;
  const create = CREATE_IDS.find(([re]) => re.test(path));
  if (method === "POST" && create && body && !body[create[1]]) body[create[1]] = genId(create[2]);
  op.body = body;
  return op;
}

const ok = (config, data) => ({ data, status: 200, statusText: "OK", headers: {}, config, request: {} });

function makeAdapter(base) {
  return async (config) => {
    const method = (config.method || "get").toUpperCase();
    const path = pathOf(config);
    if (method === "GET" && CACHEABLE.some((re) => re.test(path))) {
      try {
        const res = await base(config);
        if (res.status >= 200 && res.status < 300) {
          res.data = await overlayPending(path, parseJson(res.data));
          await idb.set("cache", path, res.data);
        }
        return res;
      } catch (e) {
        if (!isNetworkError(e)) throw e;
        const cached = await idb.get("cache", path);
        if (cached === undefined) throw e;
        return ok(config, parseJson(cached));
      }
    }
    if (!QUEUEABLE.some(([m, re]) => m === method && re.test(path))) return base(config);
    if (navigator.onLine && state.pending === 0) {
      try {
        const res = await base(config);
        await applyToCache({ method, path, body: safeBody(config.data) }, parseJson(res.data)).catch(() => {});
        return res;
      } catch (e) {
        if (!isNetworkError(e)) throw e;
      }
    }
    const op = await buildOp(config, method, path);
    const data = await applyToCache(op);
    await idb.add("outbox", op);
    await refreshPending();
    flush();
    return ok(config, data);
  };
}

function safeBody(data) {
  if (typeof data !== "string") return data instanceof FormData ? {} : data;
  try { return JSON.parse(data); } catch { return {}; }
}

// Adapters see the raw body; transformResponse only runs afterwards.
function parseJson(data) {
  if (typeof data !== "string") return data;
  try { return JSON.parse(data); } catch { return data; }
}

async function sendOp(op) {
  const headers = { "X-Client-Ts": String(op.ts) };
  if (op.token) headers.Authorization = `Bearer ${op.token}`;
  let data = op.body;
  if (op.form) {
    data = new FormData();
    const file = await idb.get("blobs", op.form.item_id);
    if (file) data.append("file", file, op.form.file_name);
    ["caption", "group_id", "item_id"].forEach((k) => op.form[k] && data.append(k, op.form[k]));
  }
  await axios({ method: op.method, url: apiBase + op.path, data, headers });
  if (op.form) await idb.del("blobs", op.form.item_id);
}

export async function flush() {
  if (flushing || !navigator.onLine) return;
  flushing = true;
  let synced = 0;
  try {
    const ops = await idb.all("outbox");
    if (ops.length) { state.syncing = true; emit(); }
    for (const op of ops) {
      try {
        await sendOp(op);
      } catch (e) {
        if (isNetworkError(e)) break;
        // Rejected by the server (deleted elsewhere, newer edit won, etc.) — drop it.
      }
      await idb.del("outbox", op.id);
      synced += 1;
    }
  } finally {
    flushing = false;
    state.syncing = false;
    await refreshPending();
    if (synced) window.dispatchEvent(new CustomEvent("notelosh:synced"));
  }
}

export async function clearOfflineCache() {
  prefetched = false;
  await idb.clear("cache").catch(() => {});
}

// Save every note (and warm the photo cache) for offline use, once per session.
let prefetched = false;
export async function prefetchAll(api) {
  if (prefetched || !navigator.onLine) return;
  prefetched = true;
  try {
    const titles = (await api.get("/titles")).data || [];
    await Promise.all(["/trash", "/reminders", "/push/prefs", "/user/settings", "/reminder-sounds"].map((p) => api.get(p).catch(() => {})));
    const photos = [];
    for (const t of titles) {
      if (!navigator.onLine) return;
      const items = (await api.get(`/titles/${t.title_id}/items`).catch(() => ({ data: [] }))).data || [];
      items.forEach((it) => { if (it.type === "file" && it.is_image && !it._local) photos.push(it.item_id); });
    }
    if (!("serviceWorker" in navigator) || !navigator.serviceWorker.controller) return;
    const auth = encodeURIComponent(tokenFn() || "");
    const queue = [...photos];
    const worker = async () => {
      while (queue.length && navigator.onLine) {
        const id = queue.shift();
        await fetch(`${apiBase}/files/${id}?auth=${auth}`, { mode: "no-cors" }).catch(() => {});
      }
    };
    await Promise.all([worker(), worker(), worker()]);
  } catch {
    prefetched = false;
  }
}

export function installOffline(api, { base, getToken }) {
  apiBase = base;
  tokenFn = getToken;
  api.defaults.adapter = makeAdapter(axios.getAdapter(axios.defaults.adapter));
  const setOnline = (v) => { state.online = v; emit(); if (v) flush(); };
  window.addEventListener("online", () => setOnline(true));
  window.addEventListener("offline", () => setOnline(false));
  setInterval(() => { if (state.pending) flush(); }, 30000);
  (async () => {
    try {
      for (const op of await idb.all("outbox")) {
        if (op.form?.item_id) {
          const f = await idb.get("blobs", op.form.item_id);
          if (f) localUrls.set(op.form.item_id, URL.createObjectURL(f));
        }
      }
      await refreshPending();
      flush();
    } catch { /* IndexedDB unavailable — app keeps working online-only */ }
  })();
}
